import {Http, URLSearchParams} from '@angular/http';
import {Observable} from 'rxjs/Rx';
import {Injectable} from '@angular/core';
import {get} from 'lodash';
import {SharingRulesGroup} from '../entity/sharing-rules-group.model';
import {SharingRule} from '../entity/sharing-rule.model';
import {Share} from '../entity/share.model';
import {DictionaryObject, Money} from '../../../../../../common/api';
import {SharePreset} from '../entity/share-preset.model';
import {isNullOrUndefined} from 'util';
import {RevenueDistributionSettingsUpdateResponse} from '../entity/revenue-distribution-settings-update-response.model';
import {ProfileKind} from '../../../api/entity/profile-kind';

@Injectable()
export class RevenueDistributionService {

    private baseUrl = '/api/backoffice';

    constructor(private _http: Http) {
    }

    public getDefaultRulesGroup(profileId: number): Observable<SharingRulesGroup> {
        return this._http.get(`${this.baseUrl}/customers/${profileId}/sharing-rules`)
            .map(response => response.json())
            .map(response => this.mapDefaultRuleGroup(response));
    }

    public getDefaultRulesGroupByKindAndIntroducer(profileId: number, kindId: number, introducerId: number): Observable<SharingRulesGroup> {
        let search: URLSearchParams = new URLSearchParams();
        search.set('kindId', String(kindId));
        search.set('parentId', !isNullOrUndefined(introducerId) ? String(introducerId) : "");

        return this._http.get(`${this.baseUrl}/customers/${profileId}/sharing-rules/kinds`, {search: search})
            .map(response => response.json())
            .map(response => this.mapDefaultRuleGroup(response));
    }

    public getExceptionRuleGroups(profileId: number): Observable<SharingRulesGroup[]> {
        return this._http.get(`${this.baseUrl}/customers/${profileId}/sharing-rules?exceptions`)
            .map(response => response.json())
            .map(response => (get(response, 'records', [])
                    .map(group => (this.mapExceptionRuleGroup(group)))
            ));
    }

    public getAvailableIntroducers(profileId: number, kindId: number, name: string): Observable<DictionaryObject[]> {
        return this._http.get(`${this.baseUrl}/customers/${profileId}/introducers?kind-id=${kindId}&name=${name}`)
            .map(response => response.json())
            .map(response => (get(response, 'items', [])));
    }

    public getAvailableSubProfiles(profileId: number): Observable<DictionaryObject[]> {
        return this._http.get(`${this.baseUrl}/customers/${profileId}/sharing-rules/profiles`)
            .map(response => response.json())
            .map(response => (get(response, 'items', [])));
    }

    public getAvailableProfileKinds(profileId: number): Observable<DictionaryObject[]> {
        return this._http.get(`${this.baseUrl}/customers/${profileId}/sharing-rules/profiles/kinds`)
            .map(response => response.json())
            .map(response => (get(response, 'items', [])));
    }

    public getProfileKindsForException(kindId: number): Observable<DictionaryObject[]> {
        return this._http.get(`${this.baseUrl}/customers/sharing-rules?profile-kind=${kindId}`)
            .map(response => response.json())
            .map(response => (get(response, 'items', [])));
    }

    public getAllAvailableProfileKinds(): Observable<ProfileKind[]> {
        return this._http.get(`${this.baseUrl}/dictionary/profile_kinds`)
            .map(response => response.json())
            .map(response => (get(response, 'records', [])));
    }

    public getAvailableFeeServices(profileId: number): Observable<DictionaryObject[]> {
        return this._http.get(`${this.baseUrl}/customers/${profileId}/sharing-rules/fee-services-condition`)
            .map(response => response.json())
            .map(response => (get(response, 'items', [])));
    }

    public getAvailableShares(profileId: number, feeId: number): Observable<SharePreset[]> {
        let search: URLSearchParams = new URLSearchParams();
        search.set('kind', 'system');
        if (!isNullOrUndefined(feeId)) {
            search.set('fee_id', String(feeId));
        }
        return this._http.get(`${this.baseUrl}/customers/${profileId}/sharing-rules/fixed-shares`, {search: search})
            .map(response => response.json())
            .map(response => (get(response, 'shares', [])));
    }

    public updateRevenueDistributionSettings(profileId: number,
                                             defaultRules: SharingRulesGroup,
                                             exceptions: SharingRulesGroup[],
                                             intId: number,
                                             kindId: number,
                                             wlTariffId: number,
                                             deletedExceptions: number[]): Observable<RevenueDistributionSettingsUpdateResponse> {
        let mapShare = (share: Share) => ({
            beneficiary_kind: share.beneficiaryKindId,
            percent: share.percent,
            amount: share.amount
        });
        let mapRule = (rule: SharingRule) => ({
            fee_id: rule.fee.id,
            shares: rule.shares.map(mapShare)
        });
        let revenueSharingGroups = {
            default_group: {
                id: defaultRules.id,
                rules: defaultRules.rules.map(mapRule)
            },
            groups_to_delete: deletedExceptions,
            exception_groups: exceptions.map(exception => ({
                id: exception.id,
                rules: exception.rules.map(mapRule),
                condition: {
                    profiles: exception.conditions.profiles.map(p => p.id),
                    profile_kinds: exception.conditions.profileKinds.map(pk => pk.id)
                }
            }))
        };
        let request = {
            kindId: kindId,
            introducer: intId,
            revenueSharingGroups: revenueSharingGroups,
            wlTariffId: wlTariffId
        };
        return this._http.patch(`${this.baseUrl}/customers/${profileId}/profile-type`, request)
            .map(response => response.json()).map(response => ({
                defaultGroup: this.mapDefaultRuleGroup(response.revenueSharingGroupsResp.default_group),
                exceptions: (get(response, 'revenueSharingGroupsResp.exception_groups', [])
                    .map(g => this.mapExceptionRuleGroup(g)))
            }));
    }

    private mapDefaultRuleGroup(group: any): SharingRulesGroup {
        return {
            name: '',
            id: group.id,
            availableBeneficiaryKinds: get(group, 'available_beneficiary_kinds', []),
            rules: this.mapRules(get(group, 'rules', [])),
            conditions: null
        };
    }

    private mapExceptionRuleGroup(group: any): SharingRulesGroup {
        return {
            name: group.name,
            id: group.id,
            availableBeneficiaryKinds: get(group, 'available_beneficiary_kinds', []),
            rules: this.mapRules(get(group, 'rules', [])),
            conditions: {
                profileKinds: get(group, 'applies_to.profile_kinds', []),
                profiles: get(group, 'applies_to.profiles', [])
            }
        };
    }

    private mapRules(rules: any[]): SharingRule[] {
        return rules.map(rule => ({
            feeId: rule.fee_id,
            feeName: rule.fee_name,
            fee: {
                id: rule.fee_id,
                name: rule.fee_name
            },
            mandatory: rule.mandatory,
            shares: this.mapShares(get(rule, 'shares', []))
        }));
    }

    private mapShares(shares: any[]): Share[] {
        return shares.map(share => ({
            percent: get(share, 'percent', 0),
            amount: get(share, 'amount', null),
            beneficiaryKind: share.beneficiary_kind.name,
            beneficiaryKindId: share.beneficiary_kind.id
        }))
    }

}



// WEBPACK FOOTER //
// ./~/angular2-template-loader!./~/angular-router-loader/src!./src/dashboard/app/shared/revenue/api/service/revenue-distribution.service.ts